/**
 * Currency formatting utilities for Indian business context
 * Consistent INR formatting across the application
 */

/**
 * Format amount in Indian Rupees with proper locale formatting
 * @param amount - The amount to format
 * @param showSymbol - Whether to show the ₹ symbol (default: true)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string
 * 
 * @example
 * formatCurrency(1234567.89) // "₹12,34,567.89"
 * formatCurrency(1234567.89, false) // "12,34,567.89"
 * formatCurrency(1234567, true, 0) // "₹12,34,567"
 */
export const formatCurrency = (
  amount: number | string,
  showSymbol: boolean = true,
  decimals: number = 2
): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  
  if (isNaN(numAmount)) {
    return showSymbol ? '₹0.00' : '0.00'
  }
  
  const formatted = numAmount.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
  
  return showSymbol ? `₹${formatted}` : formatted
}

/**
 * Format amount in compact notation (K, L, Cr)
 * @param amount - The amount to format
 * @param showSymbol - Whether to show the ₹ symbol (default: true)
 * @returns Formatted currency string in compact notation
 * 
 * @example
 * formatCurrencyCompact(1500) // "₹1.5K"
 * formatCurrencyCompact(150000) // "₹1.5L"
 * formatCurrencyCompact(15000000) // "₹1.5Cr"
 */
export const formatCurrencyCompact = (
  amount: number | string,
  showSymbol: boolean = true
): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  
  if (isNaN(numAmount)) {
    return showSymbol ? '₹0' : '0'
  }
  
  let formatted: string
  
  if (numAmount >= 10000000) {
    // Crores (1 Cr = 10,000,000)
    formatted = `${(numAmount / 10000000).toFixed(2)}Cr`
  } else if (numAmount >= 100000) {
    // Lakhs (1 L = 100,000)
    formatted = `${(numAmount / 100000).toFixed(2)}L`
  } else if (numAmount >= 1000) {
    // Thousands
    formatted = `${(numAmount / 1000).toFixed(2)}K`
  } else {
    formatted = numAmount.toFixed(2)
  }
  
  return showSymbol ? `₹${formatted}` : formatted
}

/**
 * Format amount in words (Indian numbering system)
 * @param amount - The amount to format
 * @returns Amount in words
 * 
 * @example
 * formatCurrencyInWords(1234567) // "Twelve Lakh Thirty Four Thousand Five Hundred Sixty Seven Rupees"
 */
export const formatCurrencyInWords = (amount: number | string): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  
  if (isNaN(numAmount) || numAmount === 0) {
    return 'Zero Rupees'
  }
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
  
  const convertTwoDigits = (num: number): string => {
    if (num === 0) return ''
    if (num < 10) return ones[num]
    if (num < 20) return teens[num - 10]
    return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '')
  }
  
  const convertThreeDigits = (num: number): string => {
    if (num === 0) return ''
    const hundreds = Math.floor(num / 100)
    const remainder = num % 100
    let result = hundreds > 0 ? ones[hundreds] + ' Hundred' : ''
    if (remainder > 0) {
      result += (result ? ' ' : '') + convertTwoDigits(remainder)
    }
    return result
  }
  
  const integerPart = Math.floor(numAmount)
  const decimalPart = Math.round((numAmount - integerPart) * 100)
  
  let words = ''
  
  // Crores
  const crores = Math.floor(integerPart / 10000000)
  if (crores > 0) {
    words += convertTwoDigits(crores) + ' Crore '
  }
  
  // Lakhs
  const lakhs = Math.floor((integerPart % 10000000) / 100000)
  if (lakhs > 0) {
    words += convertTwoDigits(lakhs) + ' Lakh '
  }
  
  // Thousands
  const thousands = Math.floor((integerPart % 100000) / 1000)
  if (thousands > 0) {
    words += convertTwoDigits(thousands) + ' Thousand '
  }
  
  // Hundreds, tens, ones
  const remainder = integerPart % 1000
  if (remainder > 0) {
    words += convertThreeDigits(remainder) + ' '
  }
  
  words += 'Rupees'
  
  if (decimalPart > 0) {
    words += ' and ' + convertTwoDigits(decimalPart) + ' Paise'
  }
  
  return words.trim()
}

/**
 * Parse currency string to number
 * @param currencyString - Currency string to parse (e.g., "₹1,234.56" or "1234.56")
 * @returns Parsed number
 */
export const parseCurrency = (currencyString: string): number => {
  // Remove currency symbol and commas
  const cleaned = currencyString.replace(/[₹,]/g, '').trim()
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Calculate percentage of total
 * @param amount - The amount
 * @param total - The total amount
 * @param decimals - Number of decimal places (default: 2)
 * @returns Percentage string
 */
export const formatPercentage = (
  amount: number,
  total: number,
  decimals: number = 2
): string => {
  if (total === 0) return '0%'
  const percentage = (amount / total) * 100
  return `${percentage.toFixed(decimals)}%`
}

/**
 * Format GST amount breakdown
 * @param amount - Base amount
 * @param gstRate - GST rate percentage
 * @returns Object with CGST, SGST, and total amounts
 */
export const calculateGST = (amount: number, gstRate: number) => {
  const gstAmount = (amount * gstRate) / 100
  const cgst = gstAmount / 2
  const sgst = gstAmount / 2
  const total = amount + gstAmount
  
  return {
    baseAmount: amount,
    cgst,
    sgst,
    igst: gstAmount, // For inter-state
    gstAmount,
    total,
    formatted: {
      baseAmount: formatCurrency(amount),
      cgst: formatCurrency(cgst),
      sgst: formatCurrency(sgst),
      igst: formatCurrency(gstAmount),
      gstAmount: formatCurrency(gstAmount),
      total: formatCurrency(total)
    }
  }
}
